<template>
  <div class="flex flex-col items-center p-6 max-w-5xl mx-auto w-full">
    <div class="bg-gradient-to-r from-blue-50 to-transparent dark:from-gray-800 dark:to-transparent rounded-xl p-6 mb-8 w-full">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-3 text-center">
        {{ $t("Mapping Type for") }}
        <span class="text-blue-600 dark:text-blue-400">{{ integration_name.title() }} {{ object.name.title() }}</span>
      </h2>
      <p class="text-center text-gray-600 dark:text-gray-400">
        {{ $t("Please choose how you want to map your") }} {{ integration_name.title() }} {{ object.name.title() }}
        {{ $t("object into RevSetter") }}
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
      <!-- Primary Data Source Card -->
      <div
        class="relative group cursor-pointer"
        :class="{ 'ring-2 ring-blue-500 ring-offset-2': selected_mapping === 'direct' }"
        @click="selectMappingType('direct')">
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6 h-full">
          <div class="absolute top-4 right-4">
            <div
              class="w-5 h-5 rounded-full border-2 flex items-center justify-center"
              :class="[selected_mapping === 'direct' ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-gray-600']">
              <Check v-if="selected_mapping === 'direct'" class="w-3 h-3 text-white" />
            </div>
          </div>

          <div class="mb-4">
            <div class="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg w-fit">
              <Layers class="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>

          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {{ $t("Primary Data Source") }}
          </h3>

          <div class="space-y-3 text-gray-600 dark:text-gray-400">
            <p class="text-sm">
              {{ $t("Create a new object in RevSetter or map to an existing standard object for seamless integration") }}
            </p>

            <div class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <p class="text-sm font-medium mb-2">
                {{ $t("Standard RevSetter objects") }}:
              </p>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="obj in ['Company', 'Opportunity', 'Contact']"
                  :key="obj"
                  class="px-2 py-1 bg-white dark:bg-gray-700 rounded text-xs font-medium">
                  {{ $t(obj) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Secondary Data Source Card -->
      <div
        class="relative group cursor-pointer"
        :class="{ 'ring-2 ring-blue-500 ring-offset-2': selected_mapping === 'cross' }"
        @click="selectMappingType('cross')">
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6 h-full">
          <div class="absolute top-4 right-4">
            <div
              class="w-5 h-5 rounded-full border-2 flex items-center justify-center"
              :class="[selected_mapping === 'cross' ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-gray-600']">
              <Check v-if="selected_mapping === 'cross'" class="w-3 h-3 text-white" />
            </div>
          </div>

          <div class="mb-4">
            <div class="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg w-fit">
              <SquareKanban class="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>

          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {{ $t("Secondary Data Source") }}
          </h3>

          <div class="space-y-3 text-gray-600 dark:text-gray-400">
            <p class="text-sm">
              {{ $t("Add extra metrics to existing objects in RevSetter, enriching data without creating new objects") }}
            </p>

            <div class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <p class="text-sm">
                {{ $t("Perfect for adding:") }}
              </p>
              <ul class="mt-2 space-y-1 text-sm">
                <li class="flex items-center">
                  <Plus class="w-6 h-6 mr-1 text-blue-500" />
                  {{ $t("Additional metrics") }}
                </li>
                <li class="flex items-center">
                  <Plus class="w-6 h-6 mr-1 text-blue-500" />
                  {{ $t("Supplementary data") }}
                </li>
                <li class="flex items-center">
                  <RotateCw class="w-5 h-5 mr-2 text-blue-500" />
                  {{ $t("Data updates") }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Check, RotateCw, Plus, SquareKanban, Layers } from "lucide-vue-next"

export default {
  name: "MappingType",
  components: {
    Check,
    Layers,
    SquareKanban,
    Plus,
    RotateCw
  },
  props: {
    object: {
      type: Object,
      required: true
    },
    mappingType: {
      type: String,
      default: "direct",
      required: false
    },
    currentMapping: {
      type: Object,
      default: () => ({}),
      required: false
    }
  },
  emits: ["mapping-type", "editDirectMapping"],
  data() {
    return {
      selected_mapping: null,
      integration_name: null
    }
  },
  created() {
    this.integration_name = this.$route.params.integration_name
    this.selected_mapping = this.mappingType
  },
  methods: {
    getObjectDirectMappedObjectType(object) {
      return this.currentMapping?.custom_objects?.find((o) => o.object_name == object.name)?.object_type
    },
    selectMappingType(mapping_type) {
      if (this.getObjectDirectMappedObjectType(this.object) && mapping_type == "direct") {
        this.$emit("editDirectMapping")
      } else {
        this.selected_mapping = mapping_type
        this.$emit("mapping-type", mapping_type)
      }
    }
  }
}
</script>
